import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import ReactHtmlParser from 'react-html-parser';
import { Accordion, AccordionSection, ToggleSwitch, Text } from '@oneaudi/unified-web-components';
import { breakpointsMap } from '@oneaudi/unified-web-common';
import { useGoogleConsent } from '@oneaudi/two-click';
import SearchGroup from '../SearchGroup/SearchGroup';
import searchStateContext from '../../../contexts/map-context';
import { SearchResultsGroup } from '../SearchResultsGroup/SearchResultsGroup';
import MapAutocompletion from '../MapAutocompletion/MapAutocompletion';
import DesktopDealerDetails from '../DealerDetails/DesktopDealerDetails';
import FiltersOverlay from '../FiltersOverlay/FiltersOverlay';
import { MapToggleContainer } from '../../common/TwoClickConsentModal/style';
import { UnifiedTheme } from '../../../FeatureApp';

interface SideBarProps {
  revokeConsent?: () => void | undefined;
}

export default function Sidebar(props: SideBarProps) {
  const {
    twoClickData,
    twoClickConsentRequired,
    isSidebarOpen,
    isAutocompletionOpen,
    selectedDealerId,
    results,
    isFiltersOverlayOpen,
    favoritePartner,
  } = searchStateContext.useMapState();
  const { asideToggleCopy, asideToggleLabel, asideToggleHeadline } = twoClickData;
  const consent = useGoogleConsent();
  const [toggle, setToggle] = useState(consent);
  const [open, setOpen] = useState(false);

  const { revokeConsent } = props;

  // Current iteration of WYSISYG editor does not allow author to add target attribute
  // hook is in place to add the blank target
  // Ok to remove if feature is ever added to editor in the future.
  useEffect(() => {
    const links = document.querySelectorAll('.revoke-consent > p > a');
    links.forEach((item) => item.setAttribute('target', '_blank'));
  }, []);

  useEffect(() => {
    setToggle(consent);
  }, [consent]);

  if (selectedDealerId) {
    return (
      <SidebarContainer isOpen={isSidebarOpen} data-test-id="sidebar-component">
        <DesktopDealerDetails
          dealerId={selectedDealerId}
          favoritePartner={favoritePartner}
          data-test-id="component-dealer-detail"
        />
      </SidebarContainer>
    );
  }

  if (isFiltersOverlayOpen) {
    return (
      <SidebarContainer isOpen={isSidebarOpen} data-test-id="sidebar-component">
        <FiltersOverlay data-test-id="component-filters-overlay" />
      </SidebarContainer>
    );
  }

  return (
    <SidebarContainer isOpen={isSidebarOpen} data-test-id="sidebar-component">
      <MainSidebar>
        <SearchGroup />

        {isAutocompletionOpen ? (
          <MapAutocompletion data-test-id="component-map-autocompletion" />
        ) : (
          <SearchResultsGroup results={results} data-test-id="component-search-results-group" />
        )}
        {twoClickConsentRequired && (
          <Accordion className="two-click-accordion">
            <AccordionSection
              open={open}
              onToggle={() => {
                setOpen(!open);
              }}
              headingTag="h2"
              label={asideToggleHeadline}
            >
              <MapToggleContainer id="revoke-consent-toggle" isLegacy={process.env.IS_LEGACY}>
                <ToggleSwitch
                  id="toggle"
                  name="two-click-accordion"
                  aria-labelledby="two-click-accordion"
                  onChange={() => {
                    if (revokeConsent) {
                      revokeConsent();
                      setToggle(false);
                    }
                  }}
                  value={toggle}
                  label={asideToggleLabel}
                  checked={toggle}
                />
                <Div>
                  <Text tag="span" className="revoke-consent">
                    {ReactHtmlParser(asideToggleCopy)}
                  </Text>
                </Div>
              </MapToggleContainer>
            </AccordionSection>
          </Accordion>
        )}
      </MainSidebar>
    </SidebarContainer>
  );
}

type SidebarContainerProps = { isOpen: boolean };
export const SidebarContainer = styled.div<SidebarContainerProps>`
  display: flex;
  /* position: relative; */

  height: 100%;
  width: ${breakpointsMap.sm};
  box-shadow:
    0 2px 4px 0 rgba(0, 0, 0, 0.28),
    0 0 6px 0 rgba(0, 0, 0, 0.16);
  background-color: ${(props: UnifiedTheme) =>
    props.theme.sys.color.canvas[200] || props.theme.sys.color.canvas.default};
  z-index: 30;
  transition: margin-left 0.2s cubic-bezier(0.75, 0.02, 0.5, 1);
  margin-left: ${(props) => (props.isOpen ? '0' : `- ${breakpointsMap.sm}`)};

  #two-click-accordion-container {
    position: absolute;
    bottom: 0;
    h3 {
      background-color: ${(props: UnifiedTheme) =>
        props.theme.sys.color.canvas[200] || props.theme.sys.color.canvas.default};
    }
  }
  #two-click-accordion {
    border-bottom: none;
  }

  #two-click-accordion__panel > div,
  div[id=':r1:-section'] > div {
    padding: 0 !important;
  }
`;

const MainSidebar = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Div = styled.div`
  align-self: center;
  display: grid;
`;
